import { alpha, Box, Typography, useTheme } from '@mui/material'
import { Link } from 'react-router-dom'
import EntityAvatar from 'theme/components/Avatar/EntityAvatar'
import { formatPhoneNumber } from './formatPhoneNumber'

// Icons for enhanced mode
import BadgeIcon from '@mui/icons-material/Badge'
import BusinessIcon from '@mui/icons-material/Business'
import EmailIcon from '@mui/icons-material/Email'
import LinkedInIcon from '@mui/icons-material/LinkedIn'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import NotesIcon from '@mui/icons-material/Notes'
import PersonIcon from '@mui/icons-material/Person'
import PhoneIcon from '@mui/icons-material/Phone'

interface ContactReadOnlyFieldProps {
  label: string
  name: string
  value: any
  entity?: any // The contact being displayed
  renderContext?: {
    users?: any[]
    organizations?: any[]
  }
  enhanced?: boolean // Use modern enhanced styling
  // Legacy props for backwards compatibility
  users?: any[]
  organizations?: any[]
  contact?: any
}

/**
 * Get icon based on field name for enhanced mode
 */
const getFieldIcon = (name: string): React.ReactNode | null => {
  const iconMap: Record<string, React.ReactNode> = {
    owner: <PersonIcon />,
    firstName: <BadgeIcon />,
    middleName: <BadgeIcon />,
    lastName: <BadgeIcon />,
    name: <PersonIcon />,
    title: <BadgeIcon />,
    organizations: <BusinessIcon />,
    phone: <PhoneIcon />,
    officePhone: <PhoneIcon />,
    email: <EmailIcon />,
    billingEmail: <EmailIcon />,
    linkedinProfile: <LinkedInIcon />,
    street: <LocationOnIcon />,
    city: <LocationOnIcon />,
    state: <LocationOnIcon />,
    zip: <LocationOnIcon />,
    summary: <NotesIcon />,
  }
  return iconMap[name] || null
}

const ContactReadOnlyField: React.FC<ContactReadOnlyFieldProps> = ({
  label,
  name,
  value,
  entity,
  renderContext = {},
  enhanced = true,
  users: legacyUsers,
  organizations: legacyOrganizations,
  contact: legacyContact,
}) => {
  const theme = useTheme()

  // Support both new renderContext and legacy props
  const users = renderContext.users ?? legacyUsers ?? []
  const organizations = renderContext.organizations ?? legacyOrganizations ?? []
  const contact = entity ?? legacyContact

  const getItemName = (item: any) =>
    item?.name || `${item?.firstName || ''} ${item?.lastName || ''}`.trim() || item?.email || 'Unknown'

  // Check if value is empty
  const isEmpty = value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)

  // Get icon for this field
  const icon = enhanced ? getFieldIcon(name) : null

  // Styles for enhanced mode
  const labelStyle = enhanced
    ? {
        color: theme.palette.text.secondary,
        fontWeight: 600,
        fontSize: '0.7rem',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.5px',
        mb: 0.25,
      }
    : {
        color: theme.palette.text.secondary,
        fontWeight: 500,
        fontSize: '0.875rem',
      }

  const valueStyle = {
    color: theme.palette.text.primary,
    fontWeight: 500,
    fontSize: '0.95rem',
    lineHeight: 1.5,
  }

  const emptyStyle = {
    color: alpha(theme.palette.text.secondary, 0.5),
    fontWeight: 400,
    fontSize: '0.9rem',
    fontStyle: 'italic' as const,
  }

  const linkStyle = {
    ...valueStyle,
    color: theme.palette.primary.main,
    cursor: 'pointer',
    transition: 'color 0.2s ease',
    '&:hover': {
      color: theme.palette.primary.dark,
      textDecoration: 'underline',
    },
  }

  const renderValue = () => {
    // Owner field - render user avatars
    if (name === 'owner') {
      const ownerArray = Array.isArray(value) ? value : value ? [value] : []
      const matchedUsers = ownerArray
        .map(id => users.find((user: any) => user?.user_uuid === id || user?.id === id))
        .filter(Boolean)

      if (matchedUsers.length === 0) {
        return <Typography sx={emptyStyle}>No owner assigned</Typography>
      }

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {matchedUsers.map((user: any) => (
            <Box key={user.id || user.user_uuid} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <EntityAvatar name={user.name || user.real_name || ''} photoUrl={user.image_512} size="sm" showName={false} />
              {enhanced && (
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                  {user.name || user.real_name || `${user.firstName || ''} ${user.lastName || ''}`.trim()}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )
    }

    // Related Organizations field - render as links
    if (name === 'organizations' && Array.isArray(value)) {
      // Normalize organization IDs
      const orgIdsSet = new Set(
        value
          .map((v: any) => {
            if (typeof v === 'string') return v.replace(/^ORG_/, '').toString()
            if (typeof v === 'number') return String(v)
            return (v?.org_uuid ?? v?.id ?? v?.PK?.toString()?.replace(/^ORG_/, '') ?? '').toString()
          })
          .filter(Boolean)
      )

      const matchedOrgs = organizations.filter((o: any) => {
        const candidates = [o?.org_uuid, o?.id, typeof o?.PK === 'string' ? o.PK.replace(/^ORG_/, '') : o?.PK].map(v =>
          v == null ? '' : String(v)
        )
        return candidates.some(c => orgIdsSet.has(c))
      })

      if (matchedOrgs.length === 0) {
        return <Typography sx={emptyStyle}>No related organizations</Typography>
      }

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {matchedOrgs.map((org: any) => {
            const orgIdForRoute = org?.id ?? org?.org_uuid ?? ''
            return (
              <Link
                key={org.id || org.org_uuid}
                to={`/organizations/ORG_${orgIdForRoute}/details`}
                style={{ textDecoration: 'none' }}
              >
                <Typography sx={linkStyle}>{org.name || 'Unknown Organization'}</Typography>
              </Link>
            )
          })}
        </Box>
      )
    }

    // Phone fields - format phone numbers and make clickable
    if (['phone', 'officePhone', 'workPhone'].includes(name) && value) {
      const formatted = formatPhoneNumber(value)
      return (
        <a href={`tel:${value}`} style={{ textDecoration: 'none' }}>
          <Typography sx={linkStyle}>{formatted}</Typography>
        </a>
      )
    }

    // LinkedIn field - render as link
    if (name === 'linkedinProfile' && value) {
      const url = value.startsWith('http') ? value : `https://${value}`
      return (
        <a href={url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
          <Typography sx={linkStyle}>{value}</Typography>
        </a>
      )
    }

    // Email fields - render as mailto link
    if (['email', 'billingEmail'].includes(name) && value) {
      return (
        <a href={`mailto:${value}`} style={{ textDecoration: 'none' }}>
          <Typography sx={linkStyle}>{value}</Typography>
        </a>
      )
    }

    // Empty value
    if (isEmpty) {
      return <Typography sx={emptyStyle}>Not specified</Typography>
    }

    // Default rendering
    if (Array.isArray(value)) {
      return <Typography sx={valueStyle}>{value.join(', ')}</Typography>
    }

    return <Typography sx={valueStyle}>{value}</Typography>
  }

  // Enhanced layout with icon
  if (enhanced && icon) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        {/* Icon */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: 1,
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            flexShrink: 0,
            mt: 0.25,
            '& svg': {
              fontSize: '1.1rem',
              color: theme.palette.primary.main,
            },
          }}
        >
          {icon}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={labelStyle}>{label}</Typography>
          {renderValue()}
        </Box>
      </Box>
    )
  }

  // Standard layout (no icon or not enhanced)
  return (
    <Box>
      <Typography sx={labelStyle}>{label}</Typography>
      {renderValue()}
    </Box>
  )
}

export default ContactReadOnlyField
